import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { AppContainer } from "@/domains/app";
import { ConfirmationPage } from "@/domains/appointment/confirmations";
import { DashboardWorkerPage } from "@/domains/appointment/dashboard-worker";
import { NewCalendarPage } from "@/domains/appointment/new-calendar";
import { AuditPage } from "@/domains/audit/pages/AuditPage";
import { CustomerDetailPage } from "@/domains/customer/customer-detail";
import { CustomersPage } from "@/domains/customer/customers/pages/customers-page";
import { DiscountsPage } from "@/domains/discounts";
import { ServiceEditPage } from "@/domains/menu/service-detail";
import { ServicesPage } from "@/domains/menu/services/pages/ServicesPage";
import { NotificationsPage } from "@/domains/notifications/pages/notifications-page";
import { ForgotPasswordPage } from "@/domains/onboarding/forgot-password/forgot-password-page";
import { AuthCallbackPage } from "@/domains/onboarding/handoff/auth-callback-page";
import { InvitationPage } from "@/domains/onboarding/invitations/pages";
import { LoginPage } from "@/domains/onboarding/login/login-page";
import { ResetPasswordPage } from "@/domains/onboarding/reset-password/reset-password-page";
import { SalonPage } from "@/domains/onboarding/salon/pages/salon-page";
import { SalonSelectorPage } from "@/domains/onboarding/salon-selector/salon-selector-page";
import { ReportsV2Page } from "@/domains/reports-v2";
import {
  GoogleBusinessReviewsPage,
  PlatformReviewsPage,
  ReviewsOverviewPage,
} from "@/domains/reviews";
import { SettingsLayout } from "@/domains/settings/components/settings-layout";
import { AppearanceSettingsPage } from "@/domains/settings/features/appearance";
import { SettingsAuditLogPage } from "@/domains/settings/features/audit-log/settings-audit-log-page";
import { BookingBehaviorPage } from "@/domains/settings/features/booking-behavior";
import { BusinessSettingsPage } from "@/domains/settings/features/business";
import { LimitsSettingsPage } from "@/domains/settings/features/capacity";
import { HolidaysSettingsPage } from "@/domains/settings/features/holidays";
import { MultiNotificationsSettingsPage } from "@/domains/settings/features/notifications/multi-notifications-settings-page";
import { OnlineSettingsPage } from "@/domains/settings/features/online";
import { ProfileSettingsPage } from "@/domains/settings/features/profile";
import { SalonReviewSettingsPage } from "@/domains/settings/features/reviews";
import { RewardsSettingsPage } from "@/domains/settings/features/rewards";
import { TierManagementPage } from "@/domains/settings/features/rewards/pages/tier-management-page";
import { SalonConfigSettingsPage } from "@/domains/settings/features/salon-config";
import { SchedulingSettingsPage } from "@/domains/settings/features/scheduling";
import { ServiceTemplatesSettingsPage } from "@/domains/settings/features/service-templates";
import { ShiftManagementPage } from "@/domains/settings/features/shift-management";
import { SubscriptionsSettingsPage } from "@/domains/settings/features/subscriptions";
import { TeamManagementSettingsPage } from "@/domains/settings/features/team-management";
import { WebPageSettingsPage } from "@/domains/settings/features/web-page";
import { SettingsIndexPage } from "@/domains/settings/settings-index-page";
import { WorkersPage } from "@/domains/workers/pages/workers-page";
import { ConditionalRedirect } from "@/lib/components/auth/conditional-redirect";
import { RootErrorBoundary } from "@/lib/components/RootErrorBoundary";
import { AuthGuard } from "@/lib/guard/auth-guard";
import { MobileAppLayoutProvider } from "@/lib/providers/mobile-app-layout-context";

// Legacy redirect — preserves OAuth-callback query params (?pending=1&...)
// so old bookmarks and any in-flight callbacks still land on the renamed
// settings page. The page used to be /settings/reviews; it's now
// /settings/google-business after the reviews UI moved to a top-level
// /reviews route.
function LegacySettingsReviewsRedirect() {
  const location = useLocation();
  return <Navigate to={`/settings/google-business${location.search}`} replace />;
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <AuthGuard requireAuth={false}>
        <LoginPage />
      </AuthGuard>
    ),
    errorElement: <RootErrorBoundary />,
  },
  {
    path: "/auth/callback",
    element: <AuthCallbackPage />,
    errorElement: <RootErrorBoundary />,
  },
  {
    path: "/forgot-password",
    element: (
      <AuthGuard requireAuth={false}>
        <ForgotPasswordPage />
      </AuthGuard>
    ),
    errorElement: <RootErrorBoundary />,
  },
  {
    path: "/reset-password",
    element: (
      <AuthGuard requireAuth={false}>
        <ResetPasswordPage />
      </AuthGuard>
    ),
    errorElement: <RootErrorBoundary />,
  },
  {
    path: "/salon-selector",
    element: (
      <MobileAppLayoutProvider>
        <AuthGuard requireAuth={true}>
          <SalonSelectorPage />
        </AuthGuard>
      </MobileAppLayoutProvider>
    ),
    errorElement: <RootErrorBoundary />,
  },
  {
    path: "/invite/:token",
    element: <InvitationPage />,
    errorElement: <RootErrorBoundary />,
  },
  {
    path: "/",
    element: (
      <AuthGuard requireAuth={true}>
        <AppContainer />
      </AuthGuard>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      {
        index: true,
        element: <ConditionalRedirect />,
      },
      {
        path: "appointments",
        children: [
          {
            index: true,
            element: <NewCalendarPage />,
          },
        ],
      },
      {
        path: "confirmations",
        element: <ConfirmationPage />,
      },
      {
        path: "settings",
        element: <SettingsLayout />,
        children: [
          {
            index: true,
            element: <SettingsIndexPage />,
          },
          {
            path: "profile",
            element: <ProfileSettingsPage />,
          },
          {
            path: "workers",
            element: <TeamManagementSettingsPage />,
          },
          {
            path: "shift-management",
            element: <ShiftManagementPage />,
          },
          {
            path: "appearance",
            element: <AppearanceSettingsPage />,
          },
          {
            path: "business",
            element: <BusinessSettingsPage />,
          },
          {
            path: "salon-config",
            element: <SalonConfigSettingsPage />,
          },
          {
            path: "subscriptions",
            element: <SubscriptionsSettingsPage />,
          },
          {
            path: "rewards",
            children: [
              {
                index: true,
                element: <RewardsSettingsPage />,
              },
              {
                path: "tiers",
                element: <TierManagementPage />,
              },
            ],
          },
          {
            path: "limits",
            element: <LimitsSettingsPage />,
          },
          {
            path: "booking-behavior",
            element: <BookingBehaviorPage />,
          },
          {
            path: "scheduling",
            element: <SchedulingSettingsPage />,
          },
          {
            path: "holidays",
            element: <HolidaysSettingsPage />,
          },
          {
            path: "notifications",
            element: <MultiNotificationsSettingsPage />,
          },
          {
            path: "online",
            element: <OnlineSettingsPage />,
          },
          {
            path: "reviews",
            element: <LegacySettingsReviewsRedirect />,
          },
          {
            path: "google-business",
            element: <SalonReviewSettingsPage />,
          },
          {
            path: "service-templates",
            element: <ServiceTemplatesSettingsPage />,
          },
          {
            path: "web-page",
            element: <WebPageSettingsPage />,
          },
          {
            path: "storefront",
            element: <Navigate to="/settings/web-page" replace />,
          },
          {
            path: "audit-log",
            element: <SettingsAuditLogPage />,
          },
        ],
      },
      {
        path: "services",
        element: <ServicesPage />,
      },
      {
        path: "services/:serviceId/edit",
        element: <ServiceEditPage />,
      },
      {
        path: "reports",
        element: <ReportsV2Page />,
      },
      {
        path: "customer-service",
        children: [
          {
            index: true,
            element: <ReviewsOverviewPage />,
          },
          {
            path: "platform",
            element: <PlatformReviewsPage />,
          },
          {
            path: "google-business",
            element: <GoogleBusinessReviewsPage />,
          },
        ],
      },
      // Legacy redirects — `/reviews` and its children were the path from
      // when this section was called "Reviews"; renamed to
      // "/customer-service" to match the navbar label. Keep these
      // redirects until any external bookmarks / browser history flush.
      // Also folds in the older "/reviews/gpa" alias from the GPA→Google
      // Business rename.
      {
        path: "reviews",
        element: <Navigate to="/customer-service" replace />,
      },
      {
        path: "reviews/platform",
        element: <Navigate to="/customer-service/platform" replace />,
      },
      {
        path: "reviews/google-business",
        element: <Navigate to="/customer-service/google-business" replace />,
      },
      {
        path: "reviews/gpa",
        element: <Navigate to="/customer-service/google-business" replace />,
      },
      {
        path: "notifications",
        element: <NotificationsPage />,
      },
      {
        path: "customers",
        children: [
          {
            index: true,
            element: <CustomersPage />,
          },
          {
            path: ":customerId",
            element: <CustomerDetailPage />,
          },
        ],
      },
      {
        path: "workers",
        element: <WorkersPage />,
      },
      {
        path: "discounts",
        element: <DiscountsPage />,
      },
      {
        path: "my-schedule",
        element: <DashboardWorkerPage />,
      },
      {
        path: "salon",
        element: <SalonPage />,
      },
      {
        path: "audit",
        element: <AuditPage />,
      },
    ],
  },
]);

export default router;
